import React, { useState, useEffect, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box, Paper, Typography, Divider, List, ListItem, Link,
    Alert, Chip, Stack, Skeleton, Tooltip, IconButton, Snackbar, Button,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SportsHockeyIcon from '@mui/icons-material/SportsHockey';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { User } from 'firebase/auth';
import { Game, ScoredGame } from '../types';
import { getBackendUrl } from '../config';
import { logEvent } from '../analytics';
import { authenticatedFetch, fetchWeeklyMatchups } from '../utils/apiClient';
import { CURRENT_SEASON } from '../seasons';

interface KrachData {
    predictions: Record<string, Record<string, string | null>>;
}

interface MatchupProps {
    game: ScoredGame;
    predictions?: Record<string, Record<string, string | null>>;
    seasonId: string;
}

export const getWinProbability = (homeTeam: string, visitorTeam: string, predictions: Record<string, Record<string, string | null>> | undefined): number | null => {
    if (!predictions || !predictions[homeTeam] || !predictions[homeTeam][visitorTeam]) {
        return null;
    }
    const probString = predictions[homeTeam][visitorTeam];
    if (!probString || probString === '--' || probString === 'N/A') {
        return null;
    }
    const val = parseFloat(probString);
    if (isNaN(val)) {
        return null;
    }
    return val / 100;
};

const Matchup: React.FC<MatchupProps> = ({ game, predictions, seasonId }) => {
    const { homeTeam, visitorTeam, finalScore, date, scoresByPeriod, id, gameId, isPendingFinal, isScorePending } = game.game as any;
    const winProbability = getWinProbability(homeTeam.name, visitorTeam.name, predictions);    
    const [tooltipOpen, setTooltipOpen] = useState(false);

    const getOutcome = () => {
        if (!finalScore) return { text: 'TBD', color: 'default' };
        const { homeGoals, visitorGoals } = finalScore;
        const isOvertime = Boolean((game.game as any)?.isOvertime || (scoresByPeriod && (scoresByPeriod.length > 3 || scoresByPeriod.some((p: any) => (p?.title || '').toLowerCase().includes('ot')))));
        if (homeGoals > visitorGoals) return { text: `${homeTeam.name} Wins ${homeGoals}-${visitorGoals}${isOvertime ? ' (OT)' : ''}`, color: 'info' };
        if (visitorGoals > homeGoals) return { text: `${visitorTeam.name} Wins ${visitorGoals}-${homeGoals}${isOvertime ? ' (OT)' : ''}`, color: 'info' };
        return { text: `Tie ${homeGoals}-${visitorGoals}`, color: 'warning' };
    };

    const getUpsetStatus = () => {
        if (!finalScore) return null;

        if (winProbability === null) {
            const chip = <Chip icon={<HelpOutlineIcon />} label="No Prediction" color="default" size="small" variant="outlined" />;
            return (
                <Tooltip title="Not enough prior game data to make a prediction" arrow open={tooltipOpen} onClose={() => setTooltipOpen(false)} disableFocusListener={true} disableTouchListener={true}>
                    <span onMouseEnter={() => setTooltipOpen(true)} onMouseLeave={() => setTooltipOpen(false)} onClick={() => setTooltipOpen(o => !o)}>{chip}</span>
                </Tooltip>
            );
        }

        const { homeGoals, visitorGoals } = finalScore;
        const isTie = homeGoals === visitorGoals;
        const homeWon = homeGoals > visitorGoals;
        const visitorWon = visitorGoals > homeGoals;

        const tooltipTitle = (
            <>
                <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', mb: 0.5 }}>Win Probability</Typography>
                <Typography variant="caption" display="block">{homeTeam.name}: {(winProbability * 100).toFixed(0)}%</Typography>
                <Typography variant="caption" display="block">{visitorTeam.name}: {((1 - winProbability) * 100).toFixed(0)}%</Typography>
            </>
        );

        if (winProbability === 0.5) {
            const chip = <Chip icon={<CompareArrowsIcon />} label="Toss Up" color="default" size="small" variant="outlined" />;
            return (
                <Tooltip title={tooltipTitle} arrow open={tooltipOpen} onClose={() => setTooltipOpen(false)} disableFocusListener disableTouchListener>
                    <span onMouseEnter={() => setTooltipOpen(true)} onMouseLeave={() => setTooltipOpen(false)} onClick={() => setTooltipOpen(o => !o)}>{chip}</span>
                </Tooltip>
            );
        }

        if (isTie) {
            const chip = <Chip icon={<CompareArrowsIcon />} label="Unexpected Tie" color="warning" size="small" variant="outlined" />;
            return (
                <Tooltip title={tooltipTitle} arrow open={tooltipOpen} onClose={() => setTooltipOpen(false)} disableFocusListener={true} disableTouchListener={true}>
                    <span onMouseEnter={() => setTooltipOpen(true)} onMouseLeave={() => setTooltipOpen(false)} onClick={() => setTooltipOpen(o => !o)}>
                        {chip}
                    </span>
                </Tooltip>
            );
        }

        const homeFavored = winProbability > 0.5;
        const visitorFavored = winProbability < 0.5;
        const isUpset = (homeWon && visitorFavored) || (visitorWon && homeFavored);

        const chip = isUpset ? (
            <Chip icon={<ArrowUpwardIcon />} label="Upset" color="warning" size="small" variant="outlined" />
        ) : (
            <Chip icon={<CheckCircleOutlineIcon />} label="As Expected" color="success" size="small" variant="outlined" />
        );

        return (
            <Tooltip title={tooltipTitle} arrow open={tooltipOpen} onClose={() => setTooltipOpen(false)} disableFocusListener={true} disableTouchListener={true}>
                <span onMouseEnter={() => setTooltipOpen(true)} onMouseLeave={() => setTooltipOpen(false)} onClick={() => setTooltipOpen(o => !o)}>
                    {chip}
                </span>
            </Tooltip>
        );
    };

    const getFavoredTeam = () => {
        if (finalScore) return null;
        if (winProbability === null) {
            return <Typography variant="caption" color="text.secondary">Insufficient data to predict</Typography>;
        }
        if (winProbability === 0.5) {
            return <Typography variant="caption" color="text.secondary">Toss Up</Typography>;
        }
        const favored = winProbability > 0.5 ? homeTeam.name : visitorTeam.name;
        const prob = winProbability > 0.5 ? winProbability : 1 - winProbability;
        return <Typography variant="caption" color="text.secondary">Favored: {favored} ({(prob * 100).toFixed(0)}%)</Typography>;
    };

    const outcome = getOutcome();

    const formattedDate = new Date(date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });

    const gameSheetId = finalScore ? (gameId || id) : id;
    const gameUrl = `https://gamesheetstats.com/seasons/${seasonId}/games/${gameSheetId}`;

    return (
        <ListItem sx={{ display: 'flex', flexWrap: 'nowrap', gap: 2, alignItems: 'center', py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'medium', minWidth: '40px' }}>
                {formattedDate}
            </Typography>
            <Link 
                href={gameUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                underline="hover" 
                color="inherit"
                sx={{ width: '140px', flexShrink: 0, pr: 1, '&:hover': { color: 'primary.main' } }}
            >
                <Typography variant="body2">{visitorTeam.name} at {homeTeam.name}</Typography>
            </Link>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1 }}>
                {finalScore ? (
                    <>
                        <Chip label={outcome.text} color={outcome.color as any} size="small" />
                        {isPendingFinal && (
                            <Tooltip title="Unofficial Final (Pending official review)" arrow>
                                <AccessTimeIcon
                                    sx={{ fontSize: 16, color: 'text.secondary', verticalAlign: 'middle', cursor: 'help' }}
                                    aria-label="Unofficial Final - Pending official review"
                                />
                            </Tooltip>
                        )}
                        {getUpsetStatus()}
                    </>
                ) : isScorePending ? (
                    <>
                        <Chip label="Score Pending" color="warning" size="small" variant="outlined" />
                        <Tooltip title="Game concluded — awaiting scorekeeper upload" arrow>
                            <AccessTimeIcon
                                sx={{ fontSize: 16, color: 'text.secondary', verticalAlign: 'middle', cursor: 'help' }}
                                aria-label="Score Pending - Awaiting scorekeeper upload"
                            />
                        </Tooltip>
                        {getFavoredTeam()}
                    </>
                ) : (
                     getFavoredTeam()
                )}
            </Stack>
        </ListItem>
    );
};

const SkeletonMatchup: React.FC = () => (
    <ListItem sx={{ display: 'flex', flexWrap: 'nowrap', gap: 2, alignItems: 'center', py: 1 }}>
        <Skeleton variant="text" width={40} sx={{ mr: 1 }}/>
        <Skeleton variant="text" width={140} sx={{ mr: 1 }} />
        <Skeleton variant="text" width={180} />
    </ListItem>
);

interface WeeklyMatchupsProps {
    allGames: ScoredGame[];
    user: User | null;
    fullSchedule: Game[];
    seasonId: string;
    divisionId: string;
    section?: 'both' | 'lastWeek' | 'upcoming';
}

const WeeklyMatchups: React.FC<WeeklyMatchupsProps> = ({ 
    allGames, 
    user, 
    fullSchedule, 
    seasonId, 
    divisionId,
    section = 'both'
}) => {
    const [lastWeekGames, setLastWeekGames] = useState<ScoredGame[]>([]);
    const [upcomingGames, setUpcomingGames] = useState<ScoredGame[]>([]); // This is correct
    const [lastWeekPredictions, setLastWeekPredictions] = useState<KrachData | null>(null);
    const [upcomingPredictions, setUpcomingPredictions] = useState<KrachData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    
    const { lastWeekStart, lastWeekEnd, currentWeekStart, currentWeekEnd } = useMemo(() => {
        const today = new Date();
        today.setUTCHours(0, 0, 0, 0);
        const dayOfWeek = today.getUTCDay(); // Sunday = 0, Monday = 1

        const currentWeekMonday = new Date(today);
        currentWeekMonday.setUTCDate(today.getUTCDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

        const lastWeekStart = new Date(currentWeekMonday);
        lastWeekStart.setUTCDate(currentWeekMonday.getUTCDate() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setUTCDate(lastWeekStart.getUTCDate() + 6);
        lastWeekEnd.setUTCHours(23, 59, 59, 999);

        const currentWeekEnd = new Date(currentWeekMonday);
        currentWeekEnd.setUTCDate(currentWeekMonday.getUTCDate() + 6);
        currentWeekEnd.setUTCHours(23, 59, 59, 999);

        return { lastWeekStart, lastWeekEnd, currentWeekStart: currentWeekMonday, currentWeekEnd };
    }, []);

    useEffect(() => {
        const fetchMatchupData = async () => {
            // Only show loading skeleton if we didn't have schedule data before.
            // This prevents the flash of content then back to loading.
            if (fullSchedule.length === 0) {
                setLoading(true);
            }
            setError(null);

            try {
                if (typeof fetchWeeklyMatchups === 'function') {
                    try {
                        const unified = await fetchWeeklyMatchups(seasonId, divisionId);
                        if (
                            unified &&
                            unified.seasonId === seasonId &&
                            Array.isArray(unified.lastWeek) &&
                            Array.isArray(unified.upcoming)
                        ) {
                            setLastWeekGames(unified.lastWeek.map(item => ({ game: item.game })));
                            setUpcomingGames(unified.upcoming.map(item => ({ game: item.game })));
                            setLastWeekPredictions(
                                section !== 'upcoming' ? (unified.lastWeekPredictions || null) : null
                            );
                            setUpcomingPredictions(
                                section !== 'lastWeek' ? (unified.upcomingPredictions || null) : null
                            );
                            return;
                        }
                    } catch {
                        // Transparently fall back to client schedule filtering + /api/krach
                    }
                }

                // Create a map of scores for easy lookup
                const gameDetailsMap = new Map<string, { finalScore: Game['finalScore'], scoresByPeriod: any, gameId?: string, isPendingFinal?: boolean, isScorePending?: boolean }>();
                const allGamesById = new Map(allGames.map(g => [String(g.game.gameId || g.game.id), g.game]));

                fullSchedule.forEach(scheduledGame => {
                    // The schedule API uses 'id', the scores API uses 'gameId'. They are the same value.
                    // We find the corresponding scored game using this ID.
                    const schedId = String(scheduledGame.id || scheduledGame.gameId || '');
                    if (schedId) {
                        const scoredGame = allGamesById.get(schedId);
                        if (scoredGame) {
                            // We map the score, period details, and pending status against the schedule's game ID.
                            gameDetailsMap.set(schedId, {
                                finalScore: scoredGame.finalScore,
                                scoresByPeriod: (scoredGame as any).scoresByPeriod,
                                gameId: scoredGame.gameId,
                                isPendingFinal: scoredGame.isPendingFinal ?? scheduledGame.isPendingFinal,
                            });
                        }
                    }
                });

                const lastWeek = fullSchedule.filter(g => {
                    const gameDate = new Date(g.date);
                    return gameDate >= lastWeekStart && gameDate <= lastWeekEnd;
                }).map(game => {
                    const details = gameDetailsMap.get(String(game.id || game.gameId || ''));
                    return {
                        game: {
                            ...game,
                            ...details,
                            isPendingFinal: details?.isPendingFinal ?? game.isPendingFinal,
                        }
                    };
                });

                const upcoming = fullSchedule.filter(g => {
                    const gameDate = new Date(g.date);
                    return gameDate >= currentWeekStart && gameDate <= currentWeekEnd;
                }).map(game => {
                    const details = gameDetailsMap.get(String(game.id || game.gameId || ''));
                    return {
                        game: {
                            ...game,
                            ...details,
                            isPendingFinal: details?.isPendingFinal ?? game.isPendingFinal,
                        }
                    };
                });

                setLastWeekGames(lastWeek);
                setUpcomingGames(upcoming);

                if ((lastWeek.length > 0 || upcoming.length > 0) && allGames.length > 0) {
                    const fetchKrach = async (endDate: Date): Promise<KrachData | null> => {
                        try {
                            const url = new URL(`${getBackendUrl()}/api/krach`);
                            url.searchParams.append('seasonId', seasonId);
                            url.searchParams.append('divisionId', divisionId);
                            url.searchParams.append('weighted', 'true');
                            url.searchParams.append('endDate', endDate.toISOString().split('T')[0]);
                            const res = await authenticatedFetch(url.toString());
                            if (!res.ok) return null;
                            return await res.json();
                        } catch {
                            return null;
                        }
                    };

                    // Predictions for last week's games are based on data *before* last week
                    const preLastWeek = new Date(lastWeekStart);
                    preLastWeek.setUTCDate(preLastWeek.getUTCDate() - 1);

                    // Predictions for upcoming games are based on data *before* this week
                    const preUpcomingWeek = new Date(currentWeekStart);
                    preUpcomingWeek.setUTCDate(currentWeekStart.getUTCDate() - 1);

                    const shouldFetchLastWeek = section !== 'upcoming';
                    const shouldFetchUpcoming = section !== 'lastWeek';

                    const [lastWeekPreds, upcomingPreds] = await Promise.all([
                        shouldFetchLastWeek && lastWeek.length > 0 ? fetchKrach(preLastWeek) : Promise.resolve(null),
                        shouldFetchUpcoming && upcoming.length > 0 ? fetchKrach(preUpcomingWeek) : Promise.resolve(null)
                    ]);

                    setLastWeekPredictions(lastWeekPreds);
                    setUpcomingPredictions(upcomingPreds);
                } else {
                    setLastWeekPredictions(null);
                    setUpcomingPredictions(null);
                }

            } catch (e: any) {
                setError(e.message);
                console.error("Failed to fetch matchup data:", e);
            } finally {
                setLoading(false);
            }
        };

        if (fullSchedule.length > 0) {
            fetchMatchupData();
        } else {
            setLoading(false);
        }
    }, [allGames, fullSchedule, seasonId, divisionId, section, lastWeekStart, lastWeekEnd, currentWeekStart, currentWeekEnd]);

    const handleCopyToMarkdown = (title: string, games: ScoredGame[], predictions: KrachData | null) => {
        let markdown = `### ${title}\n\n`;
        markdown += '| Date | Matchup | Result/Prediction | Analysis |\n';
        markdown += '|:---|:---|:---|:---|\n';

        const sortedGames = [...games].sort((a, b) => new Date(a.game.date).getTime() - new Date(b.game.date).getTime());

        for (const game of sortedGames) {
            const { homeTeam, visitorTeam, finalScore, date, scoresByPeriod } = game.game as any;
            const winProbability = getWinProbability(homeTeam.name, visitorTeam.name, predictions?.predictions);

            const formattedDate = new Date(date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
            const matchup = `${visitorTeam.name} at ${homeTeam.name}`;
            
            let result = '';
            let analysis = '';

            if (finalScore) {
                const { homeGoals, visitorGoals } = finalScore;
                const isOvertime = Boolean((game.game as any)?.isOvertime || (scoresByPeriod && (scoresByPeriod.length > 3 || scoresByPeriod.some((p: any) => (p?.title || '').toLowerCase().includes('ot')))));
                if (homeGoals > visitorGoals) {
                    result = `${homeTeam.name} Wins ${homeGoals}-${visitorGoals}${isOvertime ? ' (OT)' : ''}`;
                } else if (visitorGoals > homeGoals) {
                    result = `${visitorTeam.name} Wins ${visitorGoals}-${homeGoals}${isOvertime ? ' (OT)' : ''}`;
                } else {
                    result = `Tie ${homeGoals}-${visitorGoals}`;
                }

                if (winProbability === null) {
                    analysis = 'No Prediction';
                } else if (winProbability === 0.5) {
                    analysis = 'Toss Up';
                } else if (homeGoals === visitorGoals) {
                    analysis = 'Unexpected Tie';
                } else {
                    const homeWon = homeGoals > visitorGoals;
                    const visitorWon = visitorGoals > homeGoals;
                    const homeFavored = winProbability > 0.5;
                    const visitorFavored = winProbability < 0.5;
                    analysis = (homeWon && visitorFavored) || (visitorWon && homeFavored) ? 'Upset' : 'As Expected';
                }
            } else if (winProbability !== null) {
                if (winProbability === 0.5) {
                    result = 'Toss Up';
                } else {
                    const favored = winProbability > 0.5 ? homeTeam.name : visitorTeam.name;
                    const prob = winProbability > 0.5 ? winProbability : 1 - winProbability;
                    result = `Favored: ${favored} (${(prob * 100).toFixed(0)}%)`;
                }
            } else {
                result = 'Insufficient data to predict';
            }

            markdown += `| ${formattedDate} | ${matchup} | ${result} | ${analysis} |\n`;
        }

        navigator.clipboard.writeText(markdown).then(() => {
            setSnackbarOpen(true);
            logEvent('copy_weekly_matchups', { card_title: title });
        });
    };

    const renderCard = (title: string, games: ScoredGame[], predictions: KrachData | null, user: User | null) => {
        return (
            <Paper variant="outlined" elevation={0} sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
                <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <SportsHockeyIcon color="action" />
                            <Typography
                                variant="h5"
                                component="h2"
                                sx={{
                                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                    fontWeight: 600,
                                    color: 'text.primary',
                                }}
                            >
                                {title}
                            </Typography>
                        </Stack>
                        {user?.email === 'bwuest@gmail.com' && (
                            <Tooltip title="Copy as Markdown">
                                <IconButton onClick={() => handleCopyToMarkdown(title, games, predictions)} size="small">
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                </Box>
                <Divider />
                {loading ? (
                    <List dense sx={{ p: { xs: 1, sm: 2 } }}>
                        <SkeletonMatchup />
                        <SkeletonMatchup />
                        <SkeletonMatchup />
                    </List>
                ) : games.length > 0 ? (
                    <List dense sx={{ p: { xs: 1, sm: 2 }, overflowX: 'auto' }}>
                        {games.sort((a, b) => new Date(a.game.date).getTime() - new Date(b.game.date).getTime()).map(game => (
                            <Matchup key={game.game.id} game={game} predictions={predictions?.predictions} seasonId={seasonId} />
                        ))}
                    </List>
                ) : (
                    <Box sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography color="text.secondary">No games.</Typography>
                    </Box>
                )}
                <Divider />
                <Box
                    sx={{
                        px: { xs: 2, sm: 3 },
                        py: 1.5,
                        bgcolor: 'grey.50',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1,
                    }}
                >
                    <Button
                        component={RouterLink}
                        to={`/standings/${CURRENT_SEASON.key}`}
                        variant="outlined"
                        size="small"
                        onClick={() => logEvent('click_view_all_standings_matchups', { section, card_title: title })}
                    >
                        All Standings
                    </Button>
                </Box>
            </Paper>
        );
    };

    if (error) {
        return <Alert severity="warning" sx={{ mb: 3 }}>Could not load weekly matchups: {error}</Alert>;
    }

    if (section === 'lastWeek') {
        if (!loading && lastWeekGames.length === 0) {
            return null;
        }
        return (
            <>
                {renderCard("Last Week in the League", lastWeekGames, lastWeekPredictions, user as User | null)}
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={3000}
                    onClose={() => setSnackbarOpen(false)}
                    message="Matchups copied to clipboard!"
                />
            </>
        );
    }

    if (section === 'upcoming') {
        if (!loading && upcomingGames.length === 0) {
            return null;
        }
        return (
            <>
                {renderCard("Upcoming Games", upcomingGames, upcomingPredictions, user as User | null)}
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={3000}
                    onClose={() => setSnackbarOpen(false)}
                    message="Matchups copied to clipboard!"
                />
            </>
        );
    }

    // Default: section === 'both'
    // Don't render anything if loading is done and there are no games for either week.
    if (!loading && lastWeekGames.length === 0 && upcomingGames.length === 0) {
        return null;
    }

    return (
        <>
            {renderCard("Last Week in the League", lastWeekGames, lastWeekPredictions, user as User | null)}
            {renderCard("Upcoming Games", upcomingGames, upcomingPredictions, user as User | null)}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                message="Matchups copied to clipboard!"
            />
        </>
    );
};

export default WeeklyMatchups;